/**
 * Documentation editor — list of markdown docs.
 *
 * Each row stores its name (file-style — `business_rules.md`) and the
 * markdown body. Admins can add, rename, edit, delete.
 */

import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";

import { useT } from "@/i18n";
import { useTrainingRows } from "./useTrainingRows";
import type { TrainingItem } from "@/lib/training-api";

interface DocsEditorProps {
  draftKey: string;
}

export function DocsEditor({ draftKey }: DocsEditorProps): JSX.Element {
  const { t } = useT();
  const { rows, loading, error, create, update, remove } = useTrainingRows(
    draftKey,
    "documentation",
  );
  const [createError, setCreateError] = useState<string | null>(null);

  if (loading) {
    return <p className="text-sm text-neutral-500">{t("editor.ai.loading")}</p>;
  }
  if (error) {
    return <p className="text-sm text-error-dark">{error}</p>;
  }

  return (
    <div className="space-y-3">
      {rows.length === 0 ? (
        <p className="rounded-md border border-dashed border-neutral-200 px-3 py-4 text-center text-xs text-neutral-500">
          {t("editor.ai.noDocsHint")}
        </p>
      ) : (
        rows.map((row) => (
          <DocRow
            key={row.id}
            row={row}
            onSave={(name, content) => update(row.id, { name, content })}
            onDelete={() => remove(row.id)}
          />
        ))
      )}
      <button
        type="button"
        onClick={() =>
          void create({
            name: `document-${rows.length + 1}.md`,
            content: "",
            position: rows.length,
          }).then(
            () => setCreateError(null),
            (err: unknown) =>
              setCreateError(err instanceof Error ? err.message : String(err)),
          )
        }
        className="inline-flex items-center gap-1 rounded-md border border-neutral-200 bg-white px-2 py-1 text-xs font-medium text-neutral-700 hover:bg-neutral-50"
      >
        <Plus className="h-3.5 w-3.5" /> {t("editor.ai.addDocBtn")}
      </button>
      {createError && (
        <p className="text-xs text-error-dark">{createError}</p>
      )}
    </div>
  );
}

interface DocRowProps {
  row: TrainingItem;
  onSave: (name: string, content: string) => Promise<unknown>;
  onDelete: () => Promise<unknown>;
}

function DocRow({ row, onSave, onDelete }: DocRowProps): JSX.Element {
  const { t } = useT();
  const [name, setName] = useState(row.name);
  const [content, setContent] = useState(row.content);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const dirty = name !== row.name || content !== row.content;

  useEffect(() => {
    setName(row.name);
    setContent(row.content);
  }, [row.id, row.name, row.content]);

  async function handleSave(): Promise<void> {
    if (!dirty || saving) return;
    setSaving(true);
    setSaveError(null);
    try {
      await onSave(name, content);
    } catch (err) {
      // Without this, a rejected save (auth expiry, network, validation) was
      // an unhandled promise rejection with ZERO UI signal — and the
      // onBlur-save path meant the admin's edit was silently lost (P5.3).
      setSaveError(err instanceof Error ? err.message : "No se pudo guardar");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="rounded-md border border-neutral-200 bg-neutral-50 p-3">
      <div className="mb-2 flex items-center gap-2">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="block rounded-[10px] border border-control bg-white px-3 py-2 placeholder:text-neutral-500 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary flex-1 font-mono text-xs"
          placeholder={t("editor.ai.docNamePlaceholder")}
        />
        <button
          type="button"
          onClick={() => void handleSave()}
          disabled={!dirty || saving}
          className="rounded-md border border-neutral-200 bg-white px-2 py-1 text-xs font-medium text-neutral-700 hover:bg-neutral-50 disabled:opacity-40"
        >
          {saving ? t("editor.ai.saving") : t("editor.ai.saveBtn")}
        </button>
        <button
          type="button"
          onClick={() => {
            if (window.confirm(t("editor.ai.deleteConfirm"))) {
              onDelete().catch((err: unknown) =>
                setSaveError(err instanceof Error ? err.message : String(err)),
              );
            }
          }}
          className="grid h-7 w-7 place-items-center rounded text-neutral-500 hover:bg-error/10 hover:text-error-dark"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
      {/* Docs are long-form markdown — surface ~20 lines at a glance so
         the admin can read the document without expanding the textarea.
         ``resize-y`` lets them drag-grow further when needed. */}
      <textarea
        rows={20}
        value={content}
        onChange={(e) => setContent(e.target.value)}
        onBlur={() => void handleSave()}
        placeholder={t("editor.ai.docContentPlaceholder")}
        className="block w-full rounded-[10px] border border-control bg-white px-3 py-2 placeholder:text-neutral-500 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary min-h-[420px] resize-y font-mono text-xs leading-relaxed"
      />
      {saveError && (
        <p className="mt-1.5 text-xs text-error-dark">{saveError}</p>
      )}
    </div>
  );
}
